import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from '../common/ui/Button';
import './Pagination.css';

const DEFAULT_WINDOW = 5;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const buildPaginationItems = (currentPage, totalPages, windowSize = DEFAULT_WINDOW) => {
  const safeTotal = Math.max(0, Math.floor(totalPages));
  const safePage = clamp(Math.floor(currentPage), 0, Math.max(safeTotal - 1, 0));

  if (safeTotal <= 1) {
    return [];
  }

  const items = [];
  const createPage = (page) => ({ type: 'page', page });
  const createEllipsis = (id) => ({ type: 'ellipsis', id });

  const lastIndex = safeTotal - 1;
  const maxWindow = Math.max(1, windowSize);

  if (safeTotal <= maxWindow + 2) {
    for (let index = 0; index < safeTotal; index += 1) {
      items.push(createPage(index));
    }
    return items;
  }

  const isNearStart = safePage <= 2;
  const isNearEnd = safePage >= lastIndex - 2;

  if (isNearStart) {
    for (let index = 0; index < maxWindow; index += 1) {
      items.push(createPage(index));
    }
    items.push(createEllipsis('tail'));
    items.push(createPage(lastIndex));
    return items;
  }

  if (isNearEnd) {
    items.push(createPage(0));
    items.push(createEllipsis('head'));
    for (let index = lastIndex - maxWindow + 1; index <= lastIndex; index += 1) {
      items.push(createPage(index));
    }
    return items;
  }

  items.push(createPage(0));
  items.push(createEllipsis('head'));
  for (let index = safePage - 1; index <= safePage + 1; index += 1) {
    items.push(createPage(index));
  }
  items.push(createEllipsis('tail'));
  items.push(createPage(lastIndex));
  return items;
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
  className,
  ariaLabel,
}) => {
  const items = useMemo(
    () => buildPaginationItems(currentPage, totalPages, DEFAULT_WINDOW),
    [currentPage, totalPages],
  );

  const handlePageSelect = (page) => {
    if (page === currentPage || isLoading) {
      return;
    }
    onPageChange?.(page);
  };

  if (totalPages <= 1) {
    return null;
  }

  const resolvedLabel = ariaLabel || 'Pagination';

  return (
    <nav className={`pagination ${className ?? ''}`} aria-label={resolvedLabel}>
      <div className="pagination__pages" role="list">
        {items.map((item) => {
          if (item.type === 'ellipsis') {
            return (
              <span
                key={item.id}
                className="pagination__ellipsis"
                role="presentation"
                aria-hidden="true"
              >
                …
              </span>
            );
          }

          const selected = item.page === currentPage;
          return (
            <Button
              key={item.page}
              variant={selected ? 'primary' : 'ghost'}
              size="sm"
              className={`pagination__page ${selected ? 'pagination__page--active' : ''}`}
              onClick={() => handlePageSelect(item.page)}
              disabled={isLoading}
              aria-current={selected ? 'page' : undefined}
            >
              {item.page + 1}
            </Button>
          );
        })}
      </div>
    </nav>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default Pagination;
